import { useState, useEffect, useContext, useCallback } from "react";
import { ReactSortable } from "react-sortablejs";
import SettingsModal from "./SettingsModal";
import Image from "./Image";
import { StylingContext } from "../App";
import {
	getImageStore,
	migrateImageStoresFromLocalStorage,
	setImageStore
} from "../utils/imageStore";

interface ImageItem {
	id: number;
	url: string;
	label?: string;
}

const ImageHolder = () => {
	const { style } = useContext(StylingContext);

	const [images, setImages] = useState<ImageItem[]>([]);
	const [hasHydratedImages, setHasHydratedImages] = useState(false);

	const [isModalOpen, setIsModalOpen] = useState(false);

	const openModal = () => {
		setIsModalOpen(true);
	};

	const closeModal = () => {
		setIsModalOpen(false);
	};

	const compressAndDownscaleImage = useCallback(
		(base64: string, maxHeight: number, qualityPercent: number, shouldScale: boolean): Promise<string> => {
			if (qualityPercent >= 100) {
				return Promise.resolve(base64);
			}

			return new Promise((resolve) => {
				const img = document.createElement("img");
				img.src = base64;
				img.onload = function () {
					const canvas = document.createElement("canvas");
					const ctx = canvas.getContext("2d");
					if (shouldScale) {
						const scale = maxHeight / img.height;
						canvas.width = img.width * scale;
						canvas.height = maxHeight;
					} else {
						canvas.width = img.width;
						canvas.height = img.height;
					}
					ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);
					const compressedImageData = canvas.toDataURL("image/jpeg", qualityPercent / 100);
					resolve(compressedImageData);
				};
			});
		},
		[]
	);

	const handleDrop = useCallback(
		(event: DragEvent) => {
			event.preventDefault();
			if (event.dataTransfer == null) return;

			if (event.dataTransfer.getData("application/x-tier") !== "true") {
				if (event.dataTransfer.files.length > 0) {
					const time = new Date().getTime();
					const files = Array.from(event.dataTransfer.files);

					files.forEach((file, index) => {
						if (!file.type.startsWith("image/")) return;

						const label = file.name.replace(/\.[^/.]+$/, "");
						const reader = new FileReader();
						reader.onload = async function (event) {
							if (event.target == null) return;
							const imageData = event.target.result;
							const shouldScale = style.pasteScaleMode === "fixed";
							const compressedImageData = await compressAndDownscaleImage(
								imageData as string,
								style.size,
								style.quality,
								shouldScale
							);

							setImages((prevImages) => [
								...prevImages,
								{
									id: time + index,
									url: compressedImageData as string,
									label
								}
							]);
						};
						reader.readAsDataURL(file);
					});
				}
			}
		},
		[compressAndDownscaleImage, style.pasteScaleMode, style.quality, style.size]
	);

	const dragStart = useCallback((event: DragEvent) => {
		if (event.dataTransfer == null) return;
		event.dataTransfer.setData("application/x-tier", "true");
	}, []);

	const handlePaste = useCallback(
		(event: ClipboardEvent) => {
			const items = event.clipboardData?.items;
			if (!items) return;

			const time = new Date().getTime();
			for (let i = 0; i < items.length; i++) {
				const item = items[i];
				if (item.type.indexOf("image") === -1) continue;

				const blob = item.getAsFile();
				const reader = new FileReader();
				reader.readAsDataURL(blob || new Blob());

				reader.onloadend = async function () {
					const base64data = reader.result;
					const shouldScale = style.pasteScaleMode === "fixed";
					const compressedImageData = await compressAndDownscaleImage(
						base64data as string,
						style.size,
						style.quality,
						shouldScale
					);

					setImages((prevImages) => [
						...prevImages,
						{ id: time + i, url: compressedImageData }
					]);
				};
			}
		},
		[compressAndDownscaleImage, style.pasteScaleMode, style.quality, style.size]
	);

	useEffect(() => {
		let isMounted = true;

		const loadImages = async () => {
			await migrateImageStoresFromLocalStorage();
			const storedImages = await getImageStore("imageHolder");
			if (isMounted) {
				setImages(storedImages);
				setHasHydratedImages(true);
			}
		};

		loadImages();

		return () => {
			isMounted = false;
		};
	}, []);

	useEffect(() => {
		const dragOver = (event: DragEvent) => {
			event.preventDefault();
		};

		const drop = (event: DragEvent) => {
			event.preventDefault();
			handleDrop(event);
		};

		document.addEventListener("paste", handlePaste);
		document.addEventListener("dragstart", dragStart);
		document.addEventListener("dragover", dragOver);
		document.addEventListener("drop", drop);

		return () => {
			document.removeEventListener("paste", handlePaste);
			document.removeEventListener("dragstart", dragStart);
			document.removeEventListener("dragover", dragOver);
			document.removeEventListener("drop", drop);
		};
	}, [handlePaste, dragStart, handleDrop]);

	useEffect(() => {
		if (!hasHydratedImages) {
			return;
		}

		const persistImages = async () => {
			try {
				await setImageStore("imageHolder", images);
			} catch (error) {
				window.alert("Failed to save images locally. You can try deleting some images or clearing local storage in settings.");
				console.error("Failed to save images to IndexedDB:", error);
			}
		};

		persistImages();
	}, [images, hasHydratedImages]);

	return (
		<div className="bg-stone-700 flex">
			<ReactSortable
				list={images}
				setList={setImages}
				tag="div"
				group="shared"
				className="react-sortablejs flex space-x-4 p-4 min-h-[7rem] flex-wrap flex-1 items-center"
				filter=".ignore-elements"
			>
				{images.length === 0 ? (
					<p className="text-gray-400 text-center w-full ignore-elements">
						Drag & Drop or Copy and Paste images in here!
					</p>
				) : (
					images.map((image) => (
						<Image
							key={image.id}
							imageUrl={image.url}
							label={image.label}
							onDelete={() => {
								const updatedImages = images.filter(
									(img) => img.id !== image.id
								);
								setImages(updatedImages);
							}}
							onLabelChange={(newLabel) => {
								setImages((prev) =>
									prev.map((img) =>
										img.id === image.id ? { ...img, label: newLabel } : img
									)
								);
							}}
						/>
					))
				)}
			</ReactSortable>
			<button className="bg-stone-600" onClick={openModal} aria-label="Configure Images">
				<svg
					xmlns="http://www.w3.org/2000/svg"
					fill="none"
					viewBox="0 0 24 24"
					strokeWidth="1.5"
					stroke="currentColor"
					className="w-6 h-6 text-gray-400"
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z"
					/>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
					/>
				</svg>
			</button>
			<SettingsModal
				isOpen={isModalOpen}
				onClose={() => {
					closeModal();
				}}
				setImages={setImages}
			/>
		</div>
	);
};

export default ImageHolder;